import type {
  ContactFilterFieldName,
  ContactFilterQuery,
  ContactFilterRule,
  ContactQueryField,
  SerializedContactFilter,
} from "./types";

import { formatQuery } from "react-querybuilder";

import { CONTACT_FILTER_FIELDS } from "./query-builder.fields";
import { RELATIVE_TIME_OPERATORS } from "./query-builder.operators";

/**
 * A copy of the rule without the props its field does not own. The legacy
 * broadcast-only `state` and the paid-only `atLeast` would otherwise survive a
 * field change: react-querybuilder resets its own rule keys, not ours.
 */
export const dropStrayRuleProps = (
  rule: ContactFilterRule,
): ContactFilterRule => {
  const next = { ...rule };

  if (next.field !== "broadcast") delete next.state;
  if (next.field !== "paid") delete next.atLeast;

  return next;
};

export const serializeQuery = (query: ContactFilterQuery): string => {
  try {
    return encodeURIComponent(formatQuery(query, "json"));
  } catch {
    return "";
  }
};

export const deserializeQuery = (
  encoded: string | null,
): ContactFilterQuery | null => {
  if (!encoded) return null;

  try {
    const decoded = decodeURIComponent(encoded);
    const parsed = JSON.parse(decoded) as SerializedContactFilter;

    // Validate basic structure - IC format has rules array
    if (!Array.isArray(parsed.rules)) {
      return null;
    }

    return parsed as ContactFilterQuery;
  } catch {
    return null;
  }
};

export const countRules = (query: ContactFilterQuery): number => {
  let count = 0;

  for (const item of query.rules) {
    if (typeof item === "string") continue;

    if ("rules" in item) {
      count += countRules(item);
    } else if (item.field && item.operator) {
      count += 1;
    }
  }

  return count;
};

export const hasValidRules = (query: ContactFilterQuery): boolean => {
  return countRules(query) > 0;
};

/**
 * Whether a rule's value satisfies its operator. Existence operators need no
 * value; `between` needs a start+end; relative-time needs amount+unit; arrays
 * need at least one entry; everything else needs a non-empty string. Shared by
 * the contact query builder and the workflow filter builder so the notion of a
 * "complete" rule stays in one place.
 */
export const isRuleValueValid = (operator: string, value: unknown): boolean => {
  if (
    operator === "exists" ||
    operator === "doesNotExist" ||
    operator === "null" ||
    operator === "notNull"
  ) {
    return true;
  }

  if (operator === "between") {
    return Boolean(
      value &&
        typeof value === "object" &&
        "start" in value &&
        "end" in value &&
        (value as { start: unknown }).start &&
        (value as { end: unknown }).end,
    );
  }

  if (RELATIVE_TIME_OPERATORS.includes(operator)) {
    return Boolean(
      value &&
        typeof value === "object" &&
        "amount" in value &&
        "unit" in value &&
        (value as { amount: number }).amount > 0 &&
        (value as { unit: string }).unit,
    );
  }

  if (typeof value === "boolean") return true;

  if (Array.isArray(value)) return value.length > 0;

  return ((value as string | undefined) ?? "").length > 0;
};

/**
 * `fields` defaults to the static catalog so existing callers are unaffected;
 * callers that can see the workspace's own fields pass the combined list from
 * `useContactFilterFields`, since a `dynamic_*` rule is not in the catalog.
 */
export const getFieldByName = (
  name: ContactFilterFieldName | string,
  fields: ContactQueryField[] = CONTACT_FILTER_FIELDS,
): ContactQueryField | undefined => {
  return fields.find((field) => field.name === name);
};
